import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, 
  BookOpen, 
  CheckCircle2, 
  Download, 
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  Award,
  Target,
  Lightbulb,
  HelpCircle,
  CheckSquare,
  Printer,
  Calendar,
  GraduationCap,
  List,
  BookMarked,
  Layers,
  FileQuestion,
  Play,
  Eye,
  EyeOff,
  Palette,
  Heart,
  Plus,
  Package,
  Check,
  PenTool,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Progress } from "@/components/ui/progress";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import apiService from '@/services/api';
import { 
  primaryCurriculum, 
  sampleActivities, 
  assessmentSamples, 
  yearlyPlannerTemplates,
  schemeOfWorkTemplates,
  worksheetTemplates,
  lessonPlanTemplates,
  testBlueprintTemplates,
  testPapers
} from '@/data/primaryCurriculum';
import { frenchPrimaryCurriculum, frenchPrimaryWeeklyLessons } from '@/data/frenchPrimaryCurriculum';
import { PrimarySchemeOfWork } from '@/components/curriculum/PrimarySchemeOfWork';
import PrimaryLessonPlanGenerator from '@/components/curriculum/PrimaryLessonPlanGenerator';
import AILessonPlanGenerator from '@/components/curriculum/AILessonPlanGenerator';
import { useLanguage } from '@/contexts/LanguageContext';


const colorMap = {
  blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-800' },
  green: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', badge: 'bg-green-100 text-green-800' },
  purple: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', badge: 'bg-purple-100 text-purple-800' },
  orange: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', badge: 'bg-orange-100 text-orange-800' },
  red: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', badge: 'bg-red-100 text-red-800' }
};

export default function PrimaryCurriculumPage() {
  const navigate = useNavigate();
  const { isFrench, t } = useLanguage();
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("languageCommunication");
  const [activeTab, setActiveTab] = useState("curriculum");
  const [showAnswers, setShowAnswers] = useState({});
  const [teacherYears, setTeacherYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWorksheets, setSelectedWorksheets] = useState([]);
  const [includeAnswersInPack, setIncludeAnswersInPack] = useState(false);
  
  // Get curriculum data based on language
  const curriculumData = isFrench ? frenchPrimaryCurriculum : primaryCurriculum;
  const weeklyLessonsData = isFrench ? frenchPrimaryWeeklyLessons : null;
  
  // Weekly Homework Pack state
  const [showHomeworkGenerator, setShowHomeworkGenerator] = useState(false);
  const [homeworkTerm, setHomeworkTerm] = useState("1");
  const [homeworkWeek, setHomeworkWeek] = useState("1");
  const [includeAnswersInHomework, setIncludeAnswersInHomework] = useState(false);

  // Fetch teacher's assigned classes to get their year levels
  useEffect(() => {
    const fetchTeacherClasses = async () => {
      try {
        const userStr = localStorage.getItem('currentUser');
        const currentUser = userStr ? JSON.parse(userStr) : null;
        const department = currentUser?.department || '';
        const deptLower = department.toLowerCase();
        
        // Check if user is a coordinator - they see ALL years (1-6)
        const isCoordinator = currentUser?.role === 'coordinator' || 
                             deptLower.includes('coordinator') ||
                             deptLower === 'primary education';
        
        if (isCoordinator) {
          // Coordinators see all primary years (1-6)
          setTeacherYears(["Year 1", "Year 2", "Year 3", "Year 4", "Year 5", "Year 6"]);
          setSelectedYear("Year 1");
          setLoading(false);
          return;
        }
        
        // Check if teacher's department indicates specific year levels (e.g., "Year 3 Teacher", "Year 1 & 2 Teacher", "Year 5 & 6 Teacher")
        const yearMatches = department.match(/year\s*(\d)/gi);
        
        if (yearMatches && yearMatches.length > 0) {
          // Extract all year numbers from department (handles "Year 1 & 2 Teacher", "Year 3 Teacher", etc.)
          const teacherYearsList = [...new Set(yearMatches.map(match => {
            const num = match.match(/\d/)[0];
            return `Year ${num}`;
          }))].sort((a, b) => {
            const yearA = parseInt(a.replace('Year ', ''));
            const yearB = parseInt(b.replace('Year ', ''));
            return yearA - yearB;
          });
          
          setTeacherYears(teacherYearsList);
          setSelectedYear(teacherYearsList[0]);
          setLoading(false);
          return;
        }
        
        // Fallback: Try to get years from assigned classes in database
        const classes = await apiService.getTeacherClasses(true);
        // Extract unique years from teacher's classes (primary years 1-6)
        const years = [...new Set(classes
          .filter(c => c.year >= 1 && c.year <= 6)
          .map(c => `Year ${c.year}`)
        )].sort((a, b) => {
          const yearA = parseInt(a.replace('Year ', ''));
          const yearB = parseInt(b.replace('Year ', ''));
          return yearA - yearB;
        });
        
        if (years.length > 0) {
          setTeacherYears(years);
          setSelectedYear(years[0]);
        } else {
          // No classes assigned
          setTeacherYears([]);
        }
      } catch (error) {
        console.error('Error fetching teacher classes:', error);
        setTeacherYears([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTeacherClasses();
  }, []);

  // Get subjects based on language
  const subjects = isFrench ? [
    { key: "languageCommunication", label: "Langue et Communication", icon: BookOpen },
    { key: "mathematics", label: "Mathématiques", icon: Target },
    { key: "science", label: "Sciences", icon: Lightbulb },
    { key: "socialScience", label: "Sciences Sociales", icon: GraduationCap },
    { key: "artsAndCrafts", label: "Arts et Artisanat", icon: Palette },
    { key: "healthPE", label: "Santé et Éducation Physique", icon: Heart }
  ] : [
    { key: "languageCommunication", label: "Language & Communication", icon: BookOpen },
    { key: "mathematics", label: "Mathematics", icon: Target },
    { key: "science", label: "Science", icon: Lightbulb },
    { key: "livingInCommunity", label: "Social Science", icon: GraduationCap },
    { key: "artsAndCrafts", label: "Arts & Crafts", icon: Palette },
    { key: "healthPE", label: "Health & Physical Education", icon: Heart }
  ];

  const currentSubject = curriculumData[selectedSubject];

  const toggleAnswer = (questionId) => {
    setShowAnswers(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  // Navigate to Grade Book with test paper data to create an assessment
  const handleCreateAssessment = (test, term) => {
    // Get human-readable subject name
    const subjectLabel = subjects.find(s => s.key === selectedSubject)?.label || selectedSubject;
    
    // Prepare the assessment data to pass to Grade Book
    const assessmentData = {
      fromCurriculum: true,
      title: test.title,
      subject: subjectLabel,
      year: selectedYear,
      term: term,
      totalMarks: test.totalMarks,
      duration: test.duration,
      instructions: test.instructions,
      sections: test.sections,
      testId: test.id
    };

    toast.success('Redirecting to Grade Book to create assessment...');
    
    // Navigate to Grade Book with the test paper data
    navigate('/dashboard/gradebook', { state: { assessmentFromCurriculum: assessmentData } });
  };

  const printYearlyPlanner = () => {
    const planner = yearlyPlannerTemplates[selectedYear];
    const printWindow = window.open('', '_blank');
    
    const termContent = Object.entries(planner).map(([term, data]) => `
      <div class="term-section">
        <h3>${term.toUpperCase()} (Weeks ${data.weeks})</h3>
        <p><strong>Themes:</strong> ${data.themes.join(', ')}</p>
        <table>
          <tr><th>Subject</th><th>Topics</th></tr>
          ${Object.entries(data.subjects).map(([subj, topics]) => `
            <tr>
              <td>${subj.replace(/([A-Z])/g, ' $1').trim()}</td>
              <td>${topics.join(', ')}</td>
            </tr>
          `).join('')}
        </table>
      </div>
    `).join('');

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${selectedYear} Yearly Planner</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          .header { text-align: center; border-bottom: 3px solid #1e40af; padding-bottom: 15px; margin-bottom: 20px; }
          h1 { color: #1e40af; }
          h3 { color: #1e40af; margin-top: 20px; border-bottom: 1px solid #ddd; padding-bottom: 5px; }
          table { width: 100%; border-collapse: collapse; margin: 10px 0; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background: #f3f4f6; }
          .term-section { margin-bottom: 30px; page-break-inside: avoid; }
          @media print { .no-print { display: none; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>📚 ${selectedYear} Yearly Planner</h1>
          <p>Vanuatu National Curriculum - Primary Education</p>
        </div>
        ${termContent}
        <div style="margin-top: 40px; text-align: center; color: #666; font-size: 12px;">
          <p>Penama Education Portal | Generated: ${new Date().toLocaleDateString()}</p>
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  };

  const printSchemeOfWork = () => {
    const scheme = schemeOfWorkTemplates["Year 1"];
    const printWindow = window.open('', '_blank');
    
    const subjectContent = Object.entries(scheme).map(([subj, data]) => `
      <div class="subject-section">
        <h3>${subj.replace(/([A-Z])/g, ' $1').trim()}</h3>
        <table>
          <tr><th>Week</th><th>Topic</th><th>Learning Outcomes</th><th>Activities</th></tr>
          ${data.term1.map(week => `
            <tr>
              <td>${week.week}</td>
              <td>${week.topic}</td>
              <td>${week.outcomes}</td>
              <td>${week.activities}</td>
            </tr>
          `).join('')}
        </table>
      </div>
    `).join('');

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Year 1 Scheme of Work</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; font-size: 11px; }
          .header { text-align: center; border-bottom: 3px solid #1e40af; padding-bottom: 15px; margin-bottom: 20px; }
          h1 { color: #1e40af; }
          h3 { color: #1e40af; margin-top: 20px; }
          table { width: 100%; border-collapse: collapse; margin: 10px 0; }
          th, td { border: 1px solid #ddd; padding: 6px; text-align: left; }
          th { background: #f3f4f6; }
          .subject-section { margin-bottom: 30px; page-break-inside: avoid; }
          @media print { body { font-size: 10px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>📋 Year 1 Scheme of Work - Term 1</h1>
          <p>Vanuatu National Curriculum - Primary Education</p>
        </div>
        ${subjectContent}
      </body>
      </html>
    `;
    
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  };

  const printWorksheet = (worksheet, includeAnswers = false) => {
    const printWindow = window.open('', '_blank');
    
    // Build answer key section if answers are included
    let answerSection = '';
    if (includeAnswers && worksheet.answerKey) {
      answerSection = `
        <div class="answer-key">
          <h2 style="color: #166534; border-bottom: 2px solid #166534; padding-bottom: 5px;">📝 ANSWER KEY (Teacher Copy)</h2>
          ${worksheet.answerKey.answers ? `
            <div class="answers-list">
              ${Array.isArray(worksheet.answerKey.answers) ? worksheet.answerKey.answers.map((ans, i) => `
                <div class="answer-item">
                  <span class="q-num">${i + 1}.</span>
                  <span class="answer">
                    ${typeof ans === 'object' ? 
                      (ans.problem ? `${ans.problem} = <strong>${ans.answer}</strong>` : 
                       ans.word ? `${ans.word} → <strong>${ans.answer}</strong>` :
                       ans.sentence ? `${ans.sentence} → <strong>${ans.answer || ans.punctuation}</strong>` :
                       ans.item ? `${ans.item}: <strong>${ans.answer}</strong>` :
                       `<strong>${ans.answer || ans}</strong>`) 
                      : `<strong>${ans}</strong>`}
                    ${ans.working ? `<span class="working">(Working: ${ans.working})</span>` : ''}
                  </span>
                </div>
              `).join('') : ''}
            </div>
          ` : ''}
          ${worksheet.answerKey.rubric ? `
            <div class="rubric-section">
              <h3>Marking Rubric:</h3>
              ${Object.entries(worksheet.answerKey.rubric).map(([level, desc]) => `
                <p><strong>${level.charAt(0).toUpperCase() + level.slice(1)}:</strong> ${desc}</p>
              `).join('')}
            </div>
          ` : ''}
          ${worksheet.answerKey.sampleAnswer ? `
            <div class="sample-section">
              <h3>Sample Answer:</h3>
              <p class="sample">${worksheet.answerKey.sampleAnswer}</p>
            </div>
          ` : ''}
          ${worksheet.answerKey.living ? `
            <div class="classification-section">
              <p><strong>Living:</strong> ${worksheet.answerKey.living.join(', ')}</p>
              <p><strong>Non-Living:</strong> ${worksheet.answerKey.nonLiving.join(', ')}</p>
            </div>
          ` : ''}
          ${worksheet.answerKey.solid ? `
            <div class="classification-section">
              <p><strong>Solid:</strong> ${worksheet.answerKey.solid.join(', ')}</p>
              <p><strong>Liquid:</strong> ${worksheet.answerKey.liquid.join(', ')}</p>
              <p><strong>Gas:</strong> ${worksheet.answerKey.gas.join(', ')}</p>
            </div>
          ` : ''}
          ${worksheet.answerKey.renewable ? `
            <div class="classification-section">
              <p><strong>Renewable:</strong> ${worksheet.answerKey.renewable.join(', ')}</p>
              <p><strong>Non-Renewable:</strong> ${worksheet.answerKey.nonRenewable.join(', ')}</p>
              ${worksheet.answerKey.explanation ? `<p><em>${worksheet.answerKey.explanation}</em></p>` : ''}
            </div>
          ` : ''}
          ${worksheet.answerKey.vertebrates ? `
            <div class="classification-section">
              <p><strong>Vertebrates:</strong> ${worksheet.answerKey.vertebrates.join(', ')}</p>
              <p><strong>Invertebrates:</strong> ${worksheet.answerKey.invertebrates.join(', ')}</p>
            </div>
          ` : ''}
        </div>
      `;
    }
    
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${worksheet.title}${includeAnswers ? ' - TEACHER COPY' : ''}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 30px; }
          .header { border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px; }
          .header h1 { margin: 0; }
          .teacher-badge { background: #166534; color: white; padding: 4px 12px; border-radius: 4px; font-size: 12px; margin-left: 10px; }
          .name-line { margin: 20px 0; }
          .instructions { background: #f3f4f6; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
          .content { font-size: 18px; line-height: 2.5; }
          .answer-line { display: inline-block; border-bottom: 1px solid #333; width: 60px; }
          .answer-key { margin-top: 40px; padding: 20px; background: #f0fdf4; border: 2px solid #166534; border-radius: 8px; page-break-before: always; }
          .answers-list { margin: 15px 0; }
          .answer-item { margin: 10px 0; padding: 8px; background: white; border-radius: 4px; }
          .q-num { font-weight: bold; margin-right: 10px; }
          .answer { color: #166534; }
          .working { color: #666; font-size: 14px; display: block; margin-left: 25px; }
          .rubric-section { margin-top: 20px; padding: 15px; background: #fef9c3; border-radius: 8px; }
          .sample-section { margin-top: 15px; }
          .sample { background: white; padding: 15px; border-left: 4px solid #166534; font-style: italic; }
          .classification-section { margin-top: 15px; }
          @media print { .no-print { display: none; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${worksheet.title}${includeAnswers ? '<span class="teacher-badge">TEACHER COPY WITH ANSWERS</span>' : ''}</h1>
          <p>${selectedYear} - ${subjects.find(s => s.key === selectedSubject)?.label}</p>
        </div>
        ${!includeAnswers ? `
        <div class="name-line">
          <strong>Name:</strong> _________________________________ 
          <strong>Date:</strong> _________________
        </div>
        ` : ''}
        <div class="instructions">
          <strong>Instructions:</strong> ${worksheet.instructions}
        </div>
        <div class="content">
          ${worksheet.content || ''}
          ${worksheet.items ? worksheet.items.map(item => {
            if (typeof item === 'object') {
              if (item.letter) return item.letter + ': ' + (item.options || []).join(' / ') + ' → ___';
              if (item.word) return item.word + ': ' + (item.options || []).join(' / ') + ' → ___';
              if (item.animal) return item.animal + ': ' + (item.habitats || []).join(' / ') + ' → ___';
              if (item.organ) return item.organ + ': ' + (item.functions || []).join(' / ') + ' → ___';
              return JSON.stringify(item);
            }
            return item;
          }).join('<br><br>') : ''} 
          ${worksheet.problems ? worksheet.problems.join('<br><br>') : ''}
        </div>
        ${answerSection}
      </body>
      </html>
    `;
    
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  };

  // Toggle worksheet selection
  const toggleWorksheetSelection = (worksheetId) => {
    setSelectedWorksheets(prev => 
      prev.includes(worksheetId) 
        ? prev.filter(id => id !== worksheetId)
        : [...prev, worksheetId]
    );
  };

  // Select/Deselect all worksheets
  const toggleSelectAll = () => {
    const currentWorksheets = worksheetTemplates[selectedYear]?.[selectedSubject] || [];
    if (selectedWorksheets.length === currentWorksheets.length) {
      setSelectedWorksheets([]);
    } else {
      setSelectedWorksheets(currentWorksheets.map(ws => ws.id));
    }
  };

  // Generate Worksheet Pack (multiple worksheets in one printable document)
  const generateWorksheetPack = () => {
    const currentWorksheets = worksheetTemplates[selectedYear]?.[selectedSubject] || [];
    const worksheetsToInclude = currentWorksheets.filter(ws => selectedWorksheets.includes(ws.id));
    
    if (worksheetsToInclude.length === 0) {
      toast.error('Please select at least one worksheet');
      return;
    }

    const printWindow = window.open('', '_blank');
    const subjectLabel = subjects.find(s => s.key === selectedSubject)?.label || selectedSubject;
    
    // Build each worksheet section
    const worksheetSections = worksheetsToInclude.map((worksheet, index) => {
      // Build answer key section if answers are included
      let answerSection = '';
      if (includeAnswersInPack && worksheet.answerKey) {
        answerSection = `
          <div class="answer-key">
            <h3 style="color: #166534; border-bottom: 2px solid #166534; padding-bottom: 5px; margin-top: 20px;">📝 Answer Key - ${worksheet.title}</h3>
            ${worksheet.answerKey.answers ? `
              <div class="answers-list">
                ${Array.isArray(worksheet.answerKey.answers) ? worksheet.answerKey.answers.map((ans, i) => `
                  <span class="answer-item">
                    ${i + 1}. 
                    ${typeof ans === 'object' ? 
                      (ans.problem ? `${ans.problem} = <strong>${ans.answer}</strong>` : 
                       ans.word ? `${ans.word} → <strong>${ans.answer}</strong>` :
                       `<strong>${ans.answer || JSON.stringify(ans)}</strong>`) 
                      : `<strong>${ans}</strong>`}
                  </span>
                `).join(' | ') : ''}
              </div>
            ` : ''}
            ${worksheet.answerKey.living ? `
              <p><strong>Living:</strong> ${worksheet.answerKey.living.join(', ')} | <strong>Non-Living:</strong> ${worksheet.answerKey.nonLiving.join(', ')}</p>
            ` : ''}
            ${worksheet.answerKey.solid ? `
              <p><strong>Solid:</strong> ${worksheet.answerKey.solid.join(', ')} | <strong>Liquid:</strong> ${worksheet.answerKey.liquid.join(', ')} | <strong>Gas:</strong> ${worksheet.answerKey.gas.join(', ')}</p>
            ` : ''}
          </div>
        `;
      }

      return `
        <div class="worksheet-section ${index > 0 ? 'page-break' : ''}">
          <div class="worksheet-header">
            <div class="ws-number">${index + 1}</div>
            <div>
              <h2>${worksheet.title}</h2>
              <p class="worksheet-meta">${worksheet.type} | ${selectedYear} - ${subjectLabel}</p>
            </div>
          </div>
          ${!includeAnswersInPack ? `
          <div class="name-line">
            <strong>Name:</strong> _________________________________ 
            <strong>Date:</strong> _________________
          </div>
          ` : ''}
          <div class="instructions">
            <strong>Instructions:</strong> ${worksheet.instructions}
          </div>
          <div class="content">
            ${worksheet.content || ''}
            ${worksheet.items ? worksheet.items.map(item => {
              if (typeof item === 'object') {
                if (item.letter) return item.letter + ': ' + (item.options || []).join(' / ') + ' → ___';
                if (item.word) return item.word + ': ' + (item.options || []).join(' / ') + ' → ___';
                if (item.animal) return item.animal + ': ' + (item.habitats || []).join(' / ') + ' → ___';
                if (item.organ) return item.organ + ': ' + (item.functions || []).join(' / ') + ' → ___';
                return JSON.stringify(item);
              }
              return item;
            }).join('<br><br>') : ''} 
            ${worksheet.problems ? worksheet.problems.join('<br><br>') : ''}
          </div>
          ${answerSection}
        </div>
      `;
    }).join('');

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Worksheet Pack - ${selectedYear} ${subjectLabel}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          .pack-header { 
            background: linear-gradient(135deg, #3b82f6, #8b5cf6); 
            color: white; 
            padding: 20px; 
            border-radius: 12px; 
            margin-bottom: 30px;
            text-align: center;
          }
          .pack-header h1 { margin: 0; font-size: 24px; }
          .pack-header p { margin: 5px 0 0; opacity: 0.9; }
          .pack-info { 
            display: flex; 
            justify-content: center; 
            gap: 30px; 
            margin-top: 15px;
            font-size: 14px;
          }
          .pack-info span { 
            background: rgba(255,255,255,0.2); 
            padding: 5px 15px; 
            border-radius: 20px; 
          }
          .worksheet-section { margin-bottom: 40px; }
          .page-break { page-break-before: always; }
          .worksheet-header { 
            display: flex; 
            align-items: center; 
            gap: 15px;
            padding-bottom: 10px; 
            border-bottom: 3px solid #3b82f6;
            margin-bottom: 15px;
          }
          .ws-number {
            width: 40px;
            height: 40px;
            background: #3b82f6;
            color: white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            font-size: 18px;
          }
          .worksheet-header h2 { margin: 0; font-size: 20px; }
          .worksheet-meta { margin: 0; color: #666; font-size: 12px; }
          .name-line { margin: 15px 0; padding: 10px; background: #f9fafb; border-radius: 6px; }
          .instructions { background: #f3f4f6; padding: 12px; border-radius: 8px; margin-bottom: 15px; }
          .content { font-size: 16px; line-height: 2.2; }
          .answer-key { 
            margin-top: 15px; 
            padding: 15px; 
            background: #f0fdf4; 
            border: 2px solid #166534; 
            border-radius: 8px; 
          }
          .answers-list { margin: 10px 0; }
          .answer-item { display: inline-block; margin: 5px 0; }
          .toc { margin-bottom: 30px; padding: 20px; background: #f8fafc; border-radius: 8px; }
          .toc h3 { margin-top: 0; }
          .toc-item { padding: 8px 0; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; }
          .toc-item:last-child { border-bottom: none; }
          @media print { 
            .no-print { display: none; } 
            .page-break { page-break-before: always; }
          }
        </style>
      </head>
      <body>
        <div class="pack-header">
          <h1>📚 Worksheet Pack</h1>
          <p>${selectedYear} - ${subjectLabel}</p>
          <div class="pack-info">
            <span>📄 ${worksheetsToInclude.length} Worksheets</span>
            <span>📅 ${new Date().toLocaleDateString()}</span>
            ${includeAnswersInPack ? '<span>✅ Includes Answer Keys</span>' : '<span>📝 Student Copy</span>'}
          </div>
        </div>
        
        <div class="toc">
          <h3>📋 Contents</h3>
          ${worksheetsToInclude.map((ws, i) => `
            <div class="toc-item">
              <span><strong>${i + 1}.</strong> ${ws.title}</span>
              <span style="color: #666;">${ws.type}</span>
            </div>
          `).join('')}
        </div>
        
        ${worksheetSections}
        
        <div style="text-align: center; margin-top: 40px; padding: 20px; background: #f8fafc; border-radius: 8px;">
          <p style="margin: 0; color: #666;">
            Generated from Penama Education Portal | ${new Date().toLocaleString()}
          </p>
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
      toast.success(`Worksheet pack generated with ${worksheetsToInclude.length} worksheets!`);
    };
    
    // Clear selection after generating
    setSelectedWorksheets([]);
  };

  // Get lesson topics for a specific week to match with worksheets
  const getWeeklyTopics = (year, term, week) => {
    const weekNum = parseInt(week);
    const termNum = parseInt(term);
    const weekInYear = (termNum - 1) * 10 + weekNum; // Approximate week number in year
    
    const topics = [];
    const allSubjects = ['languageCommunication', 'mathematics', 'science'];
    
    allSubjects.forEach(subject => {
      const lessons = lessonPlanTemplates[year]?.[subject] || [];
      const weekLesson = lessons.find(l => l.week === weekNum || l.week === weekInYear);
      if (weekLesson) {
        topics.push({
          subject,
          subjectLabel: subjects.find(s => s.key === subject)?.label || subject,
          topic: weekLesson.topic,
          homework: weekLesson.homework,
          objectives: weekLesson.objectives || []
        });
      }
    });
    
    return topics;
  };

  // Match worksheets to weekly topics
  const getMatchingWorksheets = (year, topics) => {
    const allWorksheets = [];
    
    // Get all worksheets for this year
    const yearWorksheets = worksheetTemplates[year] || {};
    
    topics.forEach(topicInfo => {
      const subjectWorksheets = yearWorksheets[topicInfo.subject] || [];
      
      // Try to match worksheets based on topic keywords
      const topicWords = (topicInfo.topic || '').toLowerCase().split(' ');
      
      subjectWorksheets.forEach(ws => {
        const titleWords = ws.title.toLowerCase();
        const typeWords = ws.type.toLowerCase();
        const instructionWords = (ws.instructions || '').toLowerCase();
        
        // Check if any topic word matches worksheet
        const isMatch = topicWords.some(word => 
          word.length > 2 && (
            titleWords.includes(word) || 
            typeWords.includes(word) ||
            instructionWords.includes(word)
          )
        );
        
        if (isMatch && !allWorksheets.find(w => w.id === ws.id)) {
          allWorksheets.push({
            ...ws,
            matchedSubject: topicInfo.subjectLabel,
            matchedTopic: topicInfo.topic
          });
        }
      });
    });
    
    // If no matches found, include first worksheet from each subject as default
    if (allWorksheets.length === 0) {
      topics.forEach(topicInfo => {
        const subjectWorksheets = yearWorksheets[topicInfo.subject] || [];
        if (subjectWorksheets.length > 0) {
          allWorksheets.push({
            ...subjectWorksheets[0],
            matchedSubject: topicInfo.subjectLabel,
            matchedTopic: topicInfo.topic
          });
        }
      });
    }
    
    return allWorksheets;
  };

  // Generate Weekly Homework Pack
  const generateWeeklyHomeworkPack = () => {
    if (!selectedYear) {
      toast.error('Please select a year level first');
      return;
    }

    const weekNum = parseInt(homeworkWeek);
    const termNum = parseInt(homeworkTerm);
    const topics = getWeeklyTopics(selectedYear, homeworkTerm, homeworkWeek);
    const worksheetsForWeek = getMatchingWorksheets(selectedYear, topics);
    
    if (worksheetsForWeek.length === 0) {
      toast.error('No matching worksheets found for this week');
      return;
    }

    const printWindow = window.open('', '_blank');
    
    // Build worksheet sections
    const worksheetSections = worksheetsForWeek.map((worksheet, index) => {
      let answerSection = '';
      if (includeAnswersInHomework && worksheet.answerKey) {
        answerSection = `
          <div class="answer-key">
            <h3 style="color: #166534; border-bottom: 2px solid #166534; padding-bottom: 5px;">📝 Answer Key</h3>
            ${worksheet.answerKey.answers ? `
              <div class="answers-list">
                ${Array.isArray(worksheet.answerKey.answers) ? worksheet.answerKey.answers.map((ans, i) => `
                  <span class="answer-item">
                    ${i + 1}. 
                    ${typeof ans === 'object' ? 
                      (ans.problem ? `${ans.problem} = <strong>${ans.answer}</strong>` : 
                       ans.answer ? `<strong>${ans.answer}</strong>` :
                       `<strong>${JSON.stringify(ans)}</strong>`) 
                      : `<strong>${ans}</strong>`}
                  </span>
                `).join(' | ') : ''}
              </div>
            ` : ''}
            ${worksheet.answerKey.living ? `
              <p><strong>Living:</strong> ${worksheet.answerKey.living.join(', ')} | <strong>Non-Living:</strong> ${worksheet.answerKey.nonLiving.join(', ')}</p>
            ` : ''}
          </div>
        `;
      }

      return `
        <div class="worksheet-section ${index > 0 ? 'page-break' : ''}">
          <div class="worksheet-header">
            <div class="ws-number">${index + 1}</div>
            <div>
              <h2>${worksheet.title}</h2>
              <p class="worksheet-meta">${worksheet.matchedSubject} | Topic: ${worksheet.matchedTopic || 'General Practice'}</p>
            </div>
          </div>
          ${!includeAnswersInHomework ? `
          <div class="name-line">
            <strong>Name:</strong> _________________________________ 
            <strong>Date:</strong> _________________
            <strong>Parent Signature:</strong> _________________
          </div>
          ` : ''}
          <div class="instructions">
            <strong>Instructions:</strong> ${worksheet.instructions}
          </div>
          <div class="content">
            ${worksheet.content || ''}
            ${worksheet.items ? worksheet.items.map(item => {
              if (typeof item === 'object') {
                if (item.letter) return item.letter + ': ' + (item.options || []).join(' / ') + ' → ___';
                if (item.word) return item.word + ': ' + (item.options || []).join(' / ') + ' → ___';
                return JSON.stringify(item);
              }
              return item;
            }).join('<br><br>') : ''} 
            ${worksheet.problems ? worksheet.problems.join('<br><br>') : ''}
          </div>
          ${answerSection}
        </div>
      `;
    }).join('');

    // Build topics overview
    const topicsOverview = topics.map(t => `
      <div class="topic-item">
        <div class="topic-subject">${t.subjectLabel}</div>
        <div class="topic-name">${t.topic}</div>
        ${t.homework ? `<div class="topic-hw">📝 ${t.homework}</div>` : ''}
      </div>
    `).join('');

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Weekly Homework Pack - ${selectedYear} Week ${weekNum}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          .pack-header { 
            background: linear-gradient(135deg, #10b981, #3b82f6); 
            color: white; 
            padding: 25px; 
            border-radius: 16px; 
            margin-bottom: 30px;
            text-align: center;
          }
          .pack-header h1 { margin: 0; font-size: 28px; }
          .pack-header .subtitle { margin: 5px 0 0; opacity: 0.9; font-size: 18px; }
          .pack-info { 
            display: flex; 
            justify-content: center; 
            gap: 20px; 
            margin-top: 20px;
            font-size: 14px;
            flex-wrap: wrap;
          }
          .pack-info span { 
            background: rgba(255,255,255,0.2); 
            padding: 8px 16px; 
            border-radius: 20px; 
          }
          .week-overview {
            background: #f0fdf4;
            border: 2px solid #10b981;
            border-radius: 12px;
            padding: 20px;
            margin-bottom: 30px;
          }
          .week-overview h3 { margin-top: 0; color: #166534; }
          .topic-item {
            padding: 12px;
            background: white;
            border-radius: 8px;
            margin-bottom: 10px;
            border-left: 4px solid #3b82f6;
          }
          .topic-subject { font-weight: bold; color: #3b82f6; font-size: 14px; }
          .topic-name { font-size: 16px; margin: 5px 0; }
          .topic-hw { font-size: 13px; color: #666; font-style: italic; }
          .worksheet-section { margin-bottom: 40px; }
          .page-break { page-break-before: always; }
          .worksheet-header { 
            display: flex; 
            align-items: center; 
            gap: 15px;
            padding-bottom: 10px; 
            border-bottom: 3px solid #10b981;
            margin-bottom: 15px;
          }
          .ws-number {
            width: 45px;
            height: 45px;
            background: linear-gradient(135deg, #10b981, #3b82f6);
            color: white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            font-size: 20px;
          }
          .worksheet-header h2 { margin: 0; font-size: 20px; }
          .worksheet-meta { margin: 0; color: #666; font-size: 12px; }
          .name-line { 
            margin: 15px 0; 
            padding: 12px; 
            background: #f9fafb; 
            border-radius: 8px;
            border: 1px dashed #d1d5db;
          }
          .instructions { background: #f3f4f6; padding: 12px; border-radius: 8px; margin-bottom: 15px; }
          .content { font-size: 16px; line-height: 2.2; }
          .answer-key { 
            margin-top: 15px; 
            padding: 15px; 
            background: #f0fdf4; 
            border: 2px solid #166534; 
            border-radius: 8px; 
          }
          .answers-list { margin: 10px 0; }
          .answer-item { display: inline-block; margin: 5px 0; }
          .parent-note {
            background: #fef3c7;
            border: 2px solid #f59e0b;
            border-radius: 12px;
            padding: 20px;
            margin-top: 30px;
          }
          .parent-note h3 { margin-top: 0; color: #92400e; }
          .due-date {
            background: #fef2f2;
            color: #991b1b;
            padding: 10px 15px;
            border-radius: 8px;
            display: inline-block;
            font-weight: bold;
            margin-top: 10px;
          }
          @media print { 
            .no-print { display: none; } 
            .page-break { page-break-before: always; }
          }
        </style>
      </head>
      <body>
        <div class="pack-header">
          <h1>📚 Weekly Homework Pack</h1>
          <p class="subtitle">${selectedYear} | Term ${termNum} - Week ${weekNum}</p>
          <div class="pack-info">
            <span>📄 ${worksheetsForWeek.length} Worksheets</span>
            <span>📅 ${new Date().toLocaleDateString()}</span>
            <span>🎓 ${selectedYear}</span>
            ${includeAnswersInHomework ? '<span>✅ Teacher Copy</span>' : '<span>📝 Student Copy</span>'}
          </div>
        </div>
        
        <div class="week-overview">
          <h3>📖 This Week's Learning Topics</h3>
          ${topicsOverview || '<p>Topics for this week cover multiple subject areas.</p>'}
        </div>
        
        ${!includeAnswersInHomework ? `
        <div class="parent-note">
          <h3>📣 Dear Parents/Guardians</h3>
          <p>Please help your child complete the following homework worksheets. Each worksheet is designed to reinforce what your child learned in class this week.</p>
          <ul>
            <li>Encourage your child to try on their own first</li>
            <li>Help explain instructions if needed</li>
            <li>Sign each worksheet when completed</li>
          </ul>
          <div class="due-date">📅 Due: Friday of Week ${weekNum}</div>
        </div>
        ` : ''}
        
        <div class="page-break"></div>
        
        ${worksheetSections}
        
        <div style="text-align: center; margin-top: 40px; padding: 20px; background: linear-gradient(135deg, #f0fdf4, #eff6ff); border-radius: 12px;">
          <p style="margin: 0; color: #666; font-size: 14px;">
            🌴 Penama Education Portal | Weekly Homework Pack | Generated ${new Date().toLocaleString()}
          </p>
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
      toast.success(`Weekly homework pack generated with ${worksheetsForWeek.length} worksheets!`);
    };
    
    setShowHomeworkGenerator(false);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <BookMarked className="w-8 h-8 text-blue-600" />
          {isFrench ? "Ressources Pédagogiques du Programme Primaire" : "Primary Curriculum Resources"}
        </h1>
        <p className="text-gray-600 mt-2">
          {isFrench 
            ? "Programme National du Vanuatu - Années 1-6 | Objectifs d'apprentissage, activités et évaluations"
            : "Vanuatu National Curriculum - Years 1-6 | Learning outcomes, activities, and assessments"
          }
        </p>
      </div>

      {/* Year and Subject Selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Card>
          <CardContent className="pt-4">
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              {isFrench ? "Sélectionner le Niveau Scolaire" : "Select Year Level"}
            </label>
            {loading ? (
              <div className="text-sm text-gray-500">
                {isFrench ? "Chargement de vos classes..." : "Loading your classes..."}
              </div>
            ) : teacherYears.length === 0 ? (
              <div className="text-sm text-amber-600">
                {isFrench ? "Aucune classe primaire ne vous est assignée." : "No primary classes assigned to you."}
              </div>
            ) : (
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={isFrench ? "Sélectionner un niveau" : "Select a year level"} />
                </SelectTrigger>
                <SelectContent>
                  {teacherYears.map(year => (
                    <SelectItem key={year} value={year}>
                      {isFrench ? year.replace('Year', 'Année') : year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              {isFrench ? "Sélectionner la Matière" : "Select Subject"}
            </label>
            <Select value={selectedSubject} onValueChange={setSelectedSubject}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder={isFrench ? "Sélectionner une matière" : "Select a subject"} />
              </SelectTrigger>
              <SelectContent>
                {subjects.map(subj => (
                  <SelectItem key={subj.key} value={subj.key}>{subj.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-10 mb-6">
          <TabsTrigger value="curriculum" className="flex items-center gap-1">
            <BookOpen className="w-4 h-4" />
            <span className="hidden md:inline">{isFrench ? "Programme" : "Curriculum"}</span>
          </TabsTrigger>
          <TabsTrigger value="planners" className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            <span className="hidden md:inline">{isFrench ? "Planification" : "Planners"}</span>
          </TabsTrigger>
          <TabsTrigger value="schemes" className="flex items-center gap-1">
            <List className="w-4 h-4" />
            <span className="hidden md:inline">{isFrench ? "Séquences" : "Schemes"}</span>
          </TabsTrigger>
          <TabsTrigger value="tests" className="flex items-center gap-1">
            <FileQuestion className="w-4 h-4" />
            <span className="hidden md:inline">{isFrench ? "Tests" : "Tests"}</span>
          </TabsTrigger>
          <TabsTrigger value="blueprints" className="flex items-center gap-1">
            <ClipboardCheck className="w-4 h-4" />
            <span className="hidden md:inline">{isFrench ? "Barèmes" : "Blueprints"}</span>
          </TabsTrigger>
          <TabsTrigger value="activities" className="flex items-center gap-1">
            <Play className="w-4 h-4" />
            <span className="hidden md:inline">{isFrench ? "Activités" : "Activities"}</span>
          </TabsTrigger>
          <TabsTrigger value="worksheets" className="flex items-center gap-1">
            <Layers className="w-4 h-4" />
            <span className="hidden md:inline">{isFrench ? "Fiches" : "Worksheets"}</span>
          </TabsTrigger>
          <TabsTrigger value="quickprint" className="flex items-center gap-1 bg-purple-100">
            <Printer className="w-4 h-4" />
            <span className="hidden md:inline">{isFrench ? "Impression" : "Quick Print"}</span>
          </TabsTrigger>
          <TabsTrigger value="generator" className="flex items-center gap-1 bg-green-100">
            <PenTool className="w-4 h-4" />
            <span className="hidden md:inline">Lesson Generator</span>
          </TabsTrigger>
          <TabsTrigger value="ai-generator" className="flex items-center gap-1 bg-amber-100">
            <Sparkles className="w-4 h-4" />
            <span className="hidden md:inline">{isFrench ? "Plans IA" : "AI Lesson Plans"}</span>
          </TabsTrigger>
        </TabsList>

        {/* Curriculum Tab */}
        <TabsContent value="curriculum">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="w-5 h-5" />
                {currentSubject?.subject} - {isFrench ? selectedYear.replace('Year', 'Année') : selectedYear}
              </CardTitle>
              <CardDescription>{currentSubject?.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <Accordion type="multiple" className="space-y-4">
                {currentSubject?.strands?.map((strand, idx) => {
                  const colors = colorMap[strand.color] || colorMap.blue;
                  return (
                    <AccordionItem key={strand.id} value={strand.id} className={`border rounded-lg ${colors.border}`}>
                      <AccordionTrigger className={`px-4 ${colors.bg} rounded-t-lg hover:no-underline`}>
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full ${colors.badge} flex items-center justify-center font-bold`}>
                            {idx + 1}
                          </div>
                          <div className="text-left">
                            <h3 className={`font-semibold ${colors.text}`}>{strand.title}</h3>
                            <p className="text-sm text-gray-500">{strand.subStrands?.join(' • ')}</p>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 py-4">
                        <div className="space-y-4">
                          <h4 className="font-semibold text-gray-700">
                            {isFrench 
                              ? `Objectifs d'apprentissage pour ${selectedYear.replace('Year', 'Année')}:`
                              : `Learning Outcomes for ${selectedYear}:`
                            }
                          </h4>
                          <ul className="space-y-2">
                            {(isFrench 
                              ? strand.yearlyOutcomes[selectedYear.replace('Year ', 'Année ')]
                              : strand.yearlyOutcomes[selectedYear]
                            )?.map((outcome, i) => (
                              <li key={i} className="flex items-start gap-2">
                                <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                                <span>{outcome}</span>
                              </li>
                            ))}
                            {strand.yearlyOutcomes["Beginning Year 1"] && selectedYear === "Year 1" && !isFrench && (
                              <>
                                <li className="mt-4 pt-4 border-t">
                                  <p className="text-sm font-medium text-gray-500 mb-2">Beginning Year 1 Foundations:</p>
                                </li>
                                {strand.yearlyOutcomes["Beginning Year 1"].map((outcome, i) => (
                                  <li key={`beg-${i}`} className="flex items-start gap-2 text-gray-600">
                                    <Target className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                                    <span className="text-sm">{outcome}</span>
                                  </li>
                                ))}
                              </>
                            )}
                          </ul>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Lesson Plans Tab */}
        {/* Schemes of Work Tab */}
        <TabsContent value="schemes">
          {/* New Primary Scheme of Work Component with 4 Terms and Daily Lessons */}
          <PrimarySchemeOfWork 
            subject={selectedSubject === 'languageCommunication' ? 'English' : 
                     selectedSubject === 'mathematics' ? 'Mathematics' : 
                     selectedSubject === 'science' ? 'Science' : 
                     selectedSubject === 'livingInCommunity' ? 'Social Science' :
                     selectedSubject === 'artsAndCrafts' ? 'Arts and Crafts' :
                     selectedSubject === 'healthPE' ? 'Health and Physical Education' : 'English'}
            year={parseInt(selectedYear.replace('Year ', ''))}
            schoolName="Primary School"
          />
        </TabsContent>

        {/* Tests Tab - Complete Test Papers with Answer Keys */}
        <TabsContent value="tests">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileQuestion className="w-5 h-5" />
                Test Papers & Answer Keys - {selectedYear}
              </CardTitle>
              <CardDescription>Complete test papers with questions, answers, and marking guides</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {testPapers[selectedYear]?.[selectedSubject] ? (
                  Object.entries(testPapers[selectedYear][selectedSubject]).map(([term, test]) => (
                    <Card key={term} className="border-2 border-green-200">
                      <CardHeader className="bg-green-50">
                        <div className="flex items-center justify-between">
                          <div>
                            <CardTitle className="text-lg">{test.title}</CardTitle>
                            <CardDescription className="flex items-center gap-3 mt-2">
                              <Badge className="bg-green-600">{test.totalMarks} marks</Badge>
                              <Badge variant="outline">{test.duration}</Badge>
                              <Badge variant="secondary">{test.sections.length} sections</Badge>
                            </CardDescription>
                          </div>
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={() => {
                              // Print Test Paper (without answers)
                              const printWindow = window.open('', '_blank');
                              printWindow.document.write(`
                                <html>
                                <head>
                                  <title>${test.title}</title>
                                  <style>
                                    body { font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; }
                                    h1 { text-align: center; border-bottom: 2px solid #000; padding-bottom: 10px; }
                                    .header { display: flex; justify-content: space-between; margin: 20px 0; }
                                    .info { background: #f0f0f0; padding: 10px; border-radius: 5px; margin: 10px 0; }
                                    .section { margin: 20px 0; border: 1px solid #ddd; padding: 15px; }
                                    .section-title { font-weight: bold; font-size: 16px; margin-bottom: 10px; background: #e0e0e0; padding: 5px 10px; }
                                    .question { margin: 15px 0; padding: 10px; border-bottom: 1px dotted #ccc; }
                                    .options { display: flex; gap: 20px; margin-top: 10px; }
                                    .option { border: 1px solid #999; padding: 5px 15px; border-radius: 3px; }
                                    .marks { float: right; font-size: 12px; color: #666; }
                                    .answer-line { border-bottom: 1px solid #000; min-width: 200px; display: inline-block; margin: 10px 0; }
                                  </style>
                                </head>
                                <body>
                                  <h1>${test.title}</h1>
                                  <div class="header">
                                    <div>Name: _______________________</div>
                                    <div>Date: _______________________</div>
                                  </div>
                                  <div class="info">
                                    <strong>Total Marks: ${test.totalMarks}</strong> | 
                                    <strong>Time: ${test.duration}</strong>
                                  </div>
                                  <div class="info">
                                    <strong>Instructions:</strong>
                                    <ul>${test.instructions.map(i => `<li>${i}</li>`).join('')}</ul>
                                  </div>
                                  ${test.sections.map(section => `
                                    <div class="section">
                                      <div class="section-title">${section.name} (${section.marks} marks)</div>
                                      <p><em>${section.instructions}</em></p>
                                      ${section.passage ? `<p style="background:#f9f9f9;padding:10px;border-left:3px solid #666;">${section.passage}</p>` : ''}
                                      ${section.questions.map(q => `
                                        <div class="question">
                                          <span class="marks">[${q.marks} marks]</span>
                                          ${q.id}. ${q.question}
                                          ${q.options ? `<div class="options">${q.options.map(opt => `<span class="option">${opt}</span>`).join('')}</div>` : ''}
                                          ${q.type === 'writing' || q.type === 'drawing' || q.type === 'calculation' || q.type === 'word_problem' || q.type === 'spelling' || q.type === 'paragraph' ? '<div class="answer-line"></div>' : ''}
                                        </div>
                                      `).join('')}
                                    </div>
                                  `).join('')}
                                </body>
                                </html>
                              `);
                              printWindow.document.close();
                              printWindow.onload = () => printWindow.print();
                            }}>
                              <Printer className="w-4 h-4 mr-1" /> Print Test
                            </Button>
                            <Button size="sm" onClick={() => {
                              // Print Answer Key
                              const printWindow = window.open('', '_blank');
                              printWindow.document.write(`
                                <html>
                                <head>
                                  <title>Answer Key - ${test.title}</title>
                                  <style>
                                    body { font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; }
                                    h1 { text-align: center; color: #166534; border-bottom: 2px solid #166534; padding-bottom: 10px; }
                                    h2 { color: #166534; margin-top: 20px; }
                                    .section { margin: 15px 0; padding: 15px; background: #f0fdf4; border-radius: 8px; border: 1px solid #bbf7d0; }
                                    .answer { display: flex; margin: 8px 0; }
                                    .q-num { font-weight: bold; width: 30px; }
                                    .correct { color: #166534; font-weight: bold; }
                                    .marks { color: #666; font-size: 12px; margin-left: 10px; }
                                    .total { font-size: 18px; font-weight: bold; margin-top: 20px; padding: 10px; background: #166534; color: white; text-align: center; border-radius: 5px; }
                                  </style>
                                </head>
                                <body>
                                  <h1>ANSWER KEY</h1>
                                  <h2>${test.title}</h2>
                                  ${test.sections.map((section, sIdx) => `
                                    <div class="section">
                                      <h3>${section.name} (${section.marks} marks)</h3>
                                      ${section.questions.map((q, qIdx) => `
                                        <div class="answer">
                                          <span class="q-num">${q.id}.</span>
                                          <span class="correct">${q.answer}</span>
                                          <span class="marks">(${q.marks} marks)</span>
                                        </div>
                                      `).join('')}
                                    </div>
                                  `).join('')}
                                  <div class="total">TOTAL MARKS: ${test.totalMarks}</div>
                                </body>
                                </html>
                              `);
                              printWindow.document.close();
                              printWindow.onload = () => printWindow.print();
                            }}>
                              <Eye className="w-4 h-4 mr-1" /> Print Answers
                            </Button>
                            <Button 
                              size="sm" 
                              className="bg-blue-600 hover:bg-blue-700"
                              onClick={() => handleCreateAssessment(test, term)}
                              data-testid={`create-assessment-${test.id}`}
                            >
                              <Plus className="w-4 h-4 mr-1" /> Create Assessment
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-4">
                        {/* Instructions */}
                        <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                          <h4 className="font-semibold text-blue-700 mb-2">Instructions</h4>
                          <ul className="text-sm space-y-1">
                            {test.instructions.map((inst, i) => (
                              <li key={i} className="flex items-start gap-2">
                                <span className="text-blue-500">•</span>
                                {inst}
                              </li>
                            ))}
                          </ul>
                        </div>
                        
                        {/* Sections */}
                        {test.sections.map((section, sIdx) => (
                          <div key={sIdx} className="mb-6 border rounded-lg overflow-hidden">
                            <div className="bg-gray-100 px-4 py-2 flex items-center justify-between">
                              <span className="font-semibold">{section.name}</span>
                              <Badge>{section.marks} marks</Badge>
                            </div>
                            <div className="p-4">
                              <p className="text-sm text-gray-600 italic mb-3">{section.instructions}</p>
                              {section.passage && (
                                <div className="mb-4 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                                  <p className="text-sm">{section.passage}</p>
                                </div>
                              )}
                              <div className="space-y-3">
                                {section.questions.map((q, qIdx) => (
                                  <div key={qIdx} className="p-3 bg-gray-50 rounded-lg">
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <span className="font-medium">{q.id}. {q.question}</span>
                                        {q.options && (
                                          <div className="flex flex-wrap gap-2 mt-2">
                                            {q.options.map((opt, optIdx) => (
                                              <span 
                                                key={optIdx} 
                                                className={`px-3 py-1 border rounded text-sm ${
                                                  showAnswers[`${test.id}-${q.id}`] && opt === q.answer 
                                                    ? 'bg-green-100 border-green-500 font-bold' 
                                                    : 'bg-white border-gray-300'
                                                }`}
                                              >
                                                {opt}
                                              </span>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                      <Badge variant="outline" className="ml-2">{q.marks}m</Badge>
                                    </div>
                                    <div className="mt-2 flex items-center gap-2">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => toggleAnswer(`${test.id}-${q.id}`)}
                                        className="text-green-600 hover:text-green-700"
                                      >
                                        {showAnswers[`${test.id}-${q.id}`] ? (
                                          <><EyeOff className="w-4 h-4 mr-1" /> Hide</>
                                        ) : (
                                          <><Eye className="w-4 h-4 mr-1" /> Answer</>
                                        )}
                                      </Button>
                                      {showAnswers[`${test.id}-${q.id}`] && (
                                        <span className="text-green-700 font-medium">
                                          ✓ {q.answer}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                        
                        {/* Total Summary */}
                        <div className="mt-4 p-4 bg-green-100 rounded-lg flex items-center justify-between">
                          <span className="font-semibold text-green-800">Total Test Marks</span>
                          <Badge className="bg-green-600 text-lg px-4 py-1">{test.totalMarks} marks</Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <p className="text-gray-500">No test papers available for this subject yet.</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Test Blueprints Tab */}
        <TabsContent value="blueprints">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClipboardCheck className="w-5 h-5" />
                Test Blueprints - {selectedYear}
              </CardTitle>
              <CardDescription>Assessment structure and marking rubrics</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {testBlueprintTemplates[selectedYear]?.[selectedSubject] ? (
                  Object.entries(testBlueprintTemplates[selectedYear][selectedSubject]).map(([term, blueprint]) => (
                    <Card key={term} className="border-2 border-purple-200">
                      <CardHeader className="bg-purple-50">
                        <div className="flex items-center justify-between">
                          <div>
                            <CardTitle className="text-lg">{blueprint.title}</CardTitle>
                            <CardDescription className="flex items-center gap-3 mt-1">
                              <Badge variant="secondary">Total: {blueprint.totalMarks} marks</Badge>
                              <Badge variant="outline">{blueprint.duration}</Badge>
                            </CardDescription>
                          </div>
                          <Button variant="outline" size="sm" onClick={() => {
                            const printWindow = window.open('', '_blank');
                            printWindow.document.write(`
                              <html>
                              <head>
                                <title>${blueprint.title}</title>
                                <style>
                                  body { font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; }
                                  h1 { color: #7c3aed; border-bottom: 2px solid #7c3aed; padding-bottom: 10px; }
                                  .info { display: flex; gap: 20px; margin: 15px 0; }
                                  .info span { background: #f3e8ff; padding: 5px 15px; border-radius: 20px; }
                                  table { width: 100%; border-collapse: collapse; margin: 15px 0; }
                                  th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
                                  th { background: #e9d5ff; }
                                  .rubric { margin-top: 20px; }
                                  .rubric-item { display: flex; margin: 10px 0; padding: 10px; background: #f8fafc; border-radius: 8px; }
                                  .range { font-weight: bold; width: 80px; color: #7c3aed; }
                                </style>
                              </head>
                              <body>
                                <h1>${blueprint.title}</h1>
                                <div class="info">
                                  <span>Total Marks: ${blueprint.totalMarks}</span>
                                  <span>Duration: ${blueprint.duration}</span>
                                </div>
                                
                                <h2>Test Sections</h2>
                                <table>
                                  <tr>
                                    <th>Section</th>
                                    <th>Marks</th>
                                    <th>Questions</th>
                                    <th>Description</th>
                                  </tr>
                                  ${blueprint.sections.map(s => `
                                    <tr>
                                      <td><strong>${s.name}</strong></td>
                                      <td>${s.marks}</td>
                                      <td>${s.questions}</td>
                                      <td>${s.description}</td>
                                    </tr>
                                  `).join('')}
                                </table>
                                
                                <h2>Marking Rubric</h2>
                                <div class="rubric">
                                  ${Object.entries(blueprint.rubric).map(([level, data]) => `
                                    <div class="rubric-item">
                                      <span class="range">${data.range}</span>
                                      <span><strong>${level.charAt(0).toUpperCase() + level.slice(1)}:</strong> ${data.description}</span>
                                    </div>
                                  `).join('')}
                                </div>
                              </body>
                              </html>
                            `);
                            printWindow.document.close();
                            printWindow.onload = () => printWindow.print();
                          }}>
                            <Printer className="w-4 h-4 mr-1" /> Print
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-4">
                        {/* Test Sections */}
                        <div className="space-y-3 mb-6">
                          <h4 className="font-semibold text-gray-700">Test Sections</h4>
                          {blueprint.sections.map((section, idx) => (
                            <div key={idx} className="border rounded-lg p-4">
                              <div className="flex items-center justify-between mb-2">
                                <h5 className="font-semibold">{section.name}</h5>
                                <div className="flex gap-2">
                                  <Badge>{section.marks} marks</Badge>
                                  <Badge variant="outline">{section.questions} Qs</Badge>
                                </div>
                              </div>
                              <p className="text-sm text-gray-600 mb-2">{section.description}</p>
                              <div className="bg-gray-50 p-2 rounded">
                                <p className="text-xs font-medium text-gray-500 mb-1">Sample Items:</p>
                                <ul className="text-sm space-y-1">
                                  {section.sampleItems.map((item, i) => (
                                    <li key={i} className="flex items-start gap-2">
                                      <span className="text-purple-500">•</span>
                                      {item}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        {/* Rubric */}
                        <div>
                          <h4 className="font-semibold text-gray-700 mb-3">Marking Rubric</h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {Object.entries(blueprint.rubric).map(([level, data]) => (
                              <div 
                                key={level} 
                                className={`p-3 rounded-lg ${
                                  level === 'excellent' ? 'bg-green-50 border border-green-200' :
                                  level === 'good' ? 'bg-blue-50 border border-blue-200' :
                                  level === 'developing' ? 'bg-yellow-50 border border-yellow-200' :
                                  'bg-red-50 border border-red-200'
                                }`}
                              >
                                <div className="font-bold text-lg">{data.range}</div>
                                <div className="text-sm font-semibold capitalize">{level}</div>
                                <p className="text-xs text-gray-600 mt-1">{data.description}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <p className="text-gray-500">No test blueprints available for this subject yet.</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Activities Tab */}
        <TabsContent value="activities">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Play className="w-5 h-5" />
                Sample Activities - {selectedYear}
              </CardTitle>
              <CardDescription>Engaging activities aligned with curriculum outcomes</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {sampleActivities[selectedYear]?.[selectedSubject]?.map((activity, idx) => (
                  <Card key={idx} className="border-2 hover:shadow-md transition-shadow">
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <Lightbulb className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold">{activity.activity}</h4>
                          <p className="text-sm text-gray-600 mt-1">{activity.description}</p>
                          <Badge variant="outline" className="mt-2">{activity.strand}</Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )) || (
                  <p className="text-gray-500 col-span-2">No activities available for this subject yet.</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Assessments Tab */}
        {/* Planners Tab - Yearly Overview */}
        <TabsContent value="planners">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Yearly Planner - {selectedYear}
              </CardTitle>
              <CardDescription>Term-by-term overview of topics and themes for the school year</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(yearlyPlannerTemplates[selectedYear] || {}).map(([term, data]) => (
                  <Card key={term} className="border-2">
                    <CardHeader className="bg-blue-50 py-3">
                      <CardTitle className="text-lg flex items-center justify-between">
                        <span>{term.toUpperCase()}</span>
                        <Badge variant="secondary">Weeks {data.weeks}</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <div className="mb-3">
                        <h4 className="font-semibold text-gray-700 mb-2">Themes</h4>
                        <div className="flex flex-wrap gap-2">
                          {data.themes.map((theme, i) => (
                            <Badge key={i} className="bg-purple-100 text-purple-700">{theme}</Badge>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-700 mb-2">Subject Focus</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {Object.entries(data.subjects).map(([subj, topics]) => (
                            <div key={subj} className="p-3 bg-gray-50 rounded-lg">
                              <p className="font-medium text-sm text-blue-700 mb-1">
                                {subj.replace(/([A-Z])/g, ' $1').trim()}
                              </p>
                              <ul className="text-sm text-gray-600">
                                {topics.map((topic, i) => (
                                  <li key={i} className="flex items-start gap-1">
                                    <span className="text-blue-400">•</span>
                                    {topic}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <Button className="w-full mt-4" onClick={printYearlyPlanner}>
                <Printer className="w-4 h-4 mr-2" />
                Print Yearly Planner
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Worksheets Tab */}
        <TabsContent value="worksheets">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Printable Worksheets - {selectedYear}
                  </CardTitle>
                  <CardDescription>Ready-to-print worksheets for classroom use</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  {/* Weekly Homework Pack Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowHomeworkGenerator(!showHomeworkGenerator)}
                    className="bg-gradient-to-r from-green-50 to-blue-50 border-green-300 hover:from-green-100 hover:to-blue-100"
                    data-testid="weekly-homework-btn"
                  >
                    <Calendar className="w-4 h-4 mr-1 text-green-600" />
                    Weekly Homework Pack
                  </Button>
                  {worksheetTemplates[selectedYear]?.[selectedSubject]?.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={toggleSelectAll}
                      data-testid="select-all-worksheets"
                    >
                      {selectedWorksheets.length === (worksheetTemplates[selectedYear]?.[selectedSubject]?.length || 0) 
                        ? <><CheckSquare className="w-4 h-4 mr-1" /> Deselect All</>
                        : <><CheckSquare className="w-4 h-4 mr-1" /> Select All</>
                      }
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Weekly Homework Pack Generator UI */}
              {showHomeworkGenerator && (
                <div className="mt-4 p-5 bg-gradient-to-r from-green-50 to-blue-50 rounded-xl border-2 border-green-300">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-blue-500 rounded-full flex items-center justify-center">
                      <Calendar className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-gray-800">Weekly Homework Pack Generator</h3>
                      <p className="text-sm text-gray-600">Auto-generate homework based on weekly lesson topics</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                    {/* Term Selection */}
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Term</label>
                      <Select value={homeworkTerm} onValueChange={setHomeworkTerm}>
                        <SelectTrigger className="bg-white">
                          <SelectValue placeholder="Select Term" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">Term 1</SelectItem>
                          <SelectItem value="2">Term 2</SelectItem>
                          <SelectItem value="3">Term 3</SelectItem>
                          <SelectItem value="4">Term 4</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Week Selection */}
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Week</label>
                      <Select value={homeworkWeek} onValueChange={setHomeworkWeek}>
                        <SelectTrigger className="bg-white">
                          <SelectValue placeholder="Select Week" />
                        </SelectTrigger>
                        <SelectContent>
                          {[1,2,3,4,5,6,7,8,9,10].map(w => (
                            <SelectItem key={w} value={w.toString()}>Week {w}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Include Answers Toggle */}
                    <div className="flex items-end">
                      <label className="flex items-center gap-2 cursor-pointer p-2 bg-white rounded-lg border">
                        <input 
                          type="checkbox" 
                          checked={includeAnswersInHomework}
                          onChange={(e) => setIncludeAnswersInHomework(e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                        <span className="text-sm text-gray-700">Teacher Copy (with answers)</span>
                      </label>
                    </div>
                    
                    {/* Generate Button */}
                    <div className="flex items-end">
                      <Button 
                        onClick={generateWeeklyHomeworkPack}
                        className="w-full bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700"
                        disabled={!selectedYear}
                        data-testid="generate-homework-pack"
                      >
                        <Package className="w-4 h-4 mr-2" />
                        Generate Pack
                      </Button>
                    </div>
                  </div>
                  
                  {!selectedYear && (
                    <p className="text-amber-600 text-sm flex items-center gap-1">
                      <HelpCircle className="w-4 h-4" />
                      Please select a Year Level above to generate homework packs
                    </p>
                  )}
                  
                  {selectedYear && (
                    <div className="text-sm text-gray-600 bg-white p-3 rounded-lg">
                      <strong>Preview:</strong> Generating homework for <span className="text-blue-600 font-medium">{selectedYear}</span>, 
                      Term <span className="text-green-600 font-medium">{homeworkTerm}</span>, 
                      Week <span className="text-green-600 font-medium">{homeworkWeek}</span>.
                      Will include worksheets matching this week&apos;s lesson topics.
                    </div>
                  )}
                </div>
              )}
              
              {/* Worksheet Pack Generator - appears when worksheets are selected */}
              {selectedWorksheets.length > 0 && (
                <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border-2 border-blue-200">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                        <Package className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-blue-900">
                          {selectedWorksheets.length} worksheet{selectedWorksheets.length > 1 ? 's' : ''} selected
                        </p>
                        <p className="text-sm text-blue-700">Ready to generate a combined worksheet pack</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={includeAnswersInPack}
                          onChange={(e) => setIncludeAnswersInPack(e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                        <span className="text-sm text-gray-700">Include Answer Keys</span>
                      </label>
                      <Button 
                        onClick={generateWorksheetPack}
                        className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                        data-testid="generate-worksheet-pack"
                      >
                        <Package className="w-4 h-4 mr-2" />
                        Generate Pack
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {worksheetTemplates[selectedYear]?.[selectedSubject]?.map((worksheet) => (
                  <Card 
                    key={worksheet.id} 
                    className={`border-2 hover:shadow-md transition-all cursor-pointer ${
                      selectedWorksheets.includes(worksheet.id) 
                        ? 'border-blue-500 bg-blue-50/50 ring-2 ring-blue-200' 
                        : 'hover:border-gray-300'
                    }`}
                  >
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-3">
                        {/* Selection Checkbox */}
                        <div 
                          onClick={() => toggleWorksheetSelection(worksheet.id)}
                          className={`w-6 h-6 rounded border-2 flex items-center justify-center cursor-pointer transition-all ${
                            selectedWorksheets.includes(worksheet.id)
                              ? 'bg-blue-600 border-blue-600'
                              : 'border-gray-300 hover:border-blue-400'
                          }`}
                          data-testid={`select-worksheet-${worksheet.id}`}
                        >
                          {selectedWorksheets.includes(worksheet.id) && (
                            <Check className="w-4 h-4 text-white" />
                          )}
                        </div>
                        <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                          <FileText className="w-6 h-6 text-purple-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold">{worksheet.title}</h4>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline">{worksheet.type}</Badge>
                            {worksheet.answerKey && (
                              <Badge className="bg-green-100 text-green-700">Has Answers</Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 mt-2 line-clamp-2">
                            {worksheet.instructions}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-4">
                        <Button 
                          className="flex-1" 
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            printWorksheet(worksheet, false);
                          }}
                          data-testid={`print-student-${worksheet.id}`}
                        >
                          <Printer className="w-4 h-4 mr-1" />
                          Student
                        </Button>
                        {worksheet.answerKey && (
                          <Button 
                            className="flex-1 bg-green-600 hover:bg-green-700"
                            onClick={(e) => {
                              e.stopPropagation();
                              printWorksheet(worksheet, true);
                            }}
                            data-testid={`print-teacher-${worksheet.id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Teacher
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )) || (
                  <p className="text-gray-500 col-span-3">No worksheets available for this subject yet.</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Lesson Plan Generator Tab */}
        <TabsContent value="generator">
          <PrimaryLessonPlanGenerator 
            defaultYear={selectedYear || "Year 1"} 
            defaultSubject={selectedSubject === 'languageCommunication' ? 'english' : 
                           selectedSubject === 'mathematics' ? 'mathematics' : 
                           selectedSubject === 'science' ? 'science' : 
                           selectedSubject === 'livingInCommunity' ? 'socialScience' :
                           selectedSubject === 'artsAndCrafts' ? 'artsAndCrafts' :
                           selectedSubject === 'healthPE' ? 'healthPE' : 'english'}
            allowedYears={teacherYears}
          />
        </TabsContent>

        {/* AI Lesson Plan Generator Tab */}
        <TabsContent value="ai-generator">
          <AILessonPlanGenerator 
            defaultYear={selectedYear || "Year 1"} 
            defaultSubject={selectedSubject === 'languageCommunication' ? 'english' : 
                           selectedSubject === 'mathematics' ? 'mathematics' : 
                           selectedSubject === 'science' ? 'science' : 
                           selectedSubject === 'livingInCommunity' ? 'socialScience' :
                           selectedSubject === 'artsAndCrafts' ? 'artsAndCrafts' :
                           selectedSubject === 'healthPE' ? 'healthPE' : 'english'}
            allowedYears={teacherYears}
          />
        </TabsContent>

        {/* Quick Print Tab */}
        <TabsContent value="quickprint">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Printer className="w-5 h-5 text-purple-600" />
                {isFrench ? "Impression Rapide" : "Quick Print"}
              </CardTitle>
              <CardDescription>
                {isFrench 
                  ? "Imprimez le pack complet du trimestre incluant le programme de travail et tous les plans de leçons hebdomadaires"
                  : "Print complete term pack including Scheme of Work and all weekly Lesson Plans"
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Quick Print All */}
              <Card className="border-2 border-purple-200 bg-purple-50">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-purple-600 flex items-center justify-center">
                      <Printer className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-purple-900">
                        {isFrench ? "Impression Complète" : "Quick Print All"}
                      </h3>
                      <p className="text-purple-700 mt-1">
                        {isFrench 
                          ? "Imprimez le pack complet du trimestre incluant le programme de travail et tous les plans de leçons hebdomadaires"
                          : "Print complete term pack including Scheme of Work and all weekly Lesson Plans"
                        }
                      </p>
                      <Button 
                        className="mt-4 bg-purple-600 hover:bg-purple-700"
                        onClick={() => {
                          const subjectLabel = subjects.find(s => s.key === selectedSubject)?.label || selectedSubject;
                          const printWindow = window.open('', '_blank');
                          
                          const html = `
                            <!DOCTYPE html>
                            <html>
                            <head>
                              <title>${selectedYear} - ${subjectLabel} - Complete Term Pack</title>
                              <style>
                                body { font-family: Arial, sans-serif; padding: 20px; }
                                .header { text-align: center; border-bottom: 3px solid #7c3aed; padding-bottom: 20px; margin-bottom: 30px; }
                                .header h1 { color: #7c3aed; margin: 0; }
                                .header p { color: #666; margin: 5px 0; }
                                .section { margin-bottom: 40px; page-break-inside: avoid; }
                                .section-title { background: #7c3aed; color: white; padding: 10px 15px; border-radius: 8px 8px 0 0; }
                                .section-content { border: 1px solid #e5e7eb; border-top: none; padding: 20px; border-radius: 0 0 8px 8px; }
                                table { width: 100%; border-collapse: collapse; margin: 15px 0; }
                                th, td { border: 1px solid #d1d5db; padding: 10px; text-align: left; }
                                th { background: #f3f4f6; font-weight: bold; }
                                .week-header { background: #ede9fe; font-weight: bold; }
                                .footer { text-align: center; margin-top: 40px; color: #666; font-size: 12px; border-top: 1px solid #e5e7eb; padding-top: 20px; }
                                @media print { .page-break { page-break-before: always; } }
                              </style>
                            </head>
                            <body>
                              <div class="header">
                                <h1>📚 ${selectedYear} - ${subjectLabel}</h1>
                                <p>Complete Term Pack | Vanuatu National Curriculum</p>
                                <p>Generated: ${new Date().toLocaleDateString()}</p>
                              </div>
                              
                              <div class="section">
                                <div class="section-title">📋 Scheme of Work - Term 1</div>
                                <div class="section-content">
                                  <table>
                                    <tr>
                                      <th>Week</th>
                                      <th>Topic</th>
                                      <th>Learning Outcomes</th>
                                      <th>Activities</th>
                                      <th>Assessment</th>
                                    </tr>
                                    ${[1,2,3,4,5,6,7,8,9,10].map(week => `
                                      <tr>
                                        <td class="week-header">Week ${week}</td>
                                        <td>Topic ${week}</td>
                                        <td>Students will be able to...</td>
                                        <td>Class activities, group work</td>
                                        <td>Formative assessment</td>
                                      </tr>
                                    `).join('')}
                                  </table>
                                </div>
                              </div>
                              
                              <div class="section page-break">
                                <div class="section-title">📝 Weekly Lesson Plans</div>
                                <div class="section-content">
                                  ${[1,2,3,4,5,6,7,8,9,10].map(week => `
                                    <div style="margin-bottom: 30px; ${week > 1 ? 'page-break-before: always;' : ''}">
                                      <h3 style="color: #7c3aed; border-bottom: 2px solid #7c3aed; padding-bottom: 5px;">
                                        Week ${week} Lesson Plan
                                      </h3>
                                      <table>
                                        <tr><th width="150">Subject</th><td>${subjectLabel}</td></tr>
                                        <tr><th>Year Level</th><td>${selectedYear}</td></tr>
                                        <tr><th>Week</th><td>Week ${week}, Term 1</td></tr>
                                        <tr><th>Duration</th><td>5 days (40 minutes per lesson)</td></tr>
                                        <tr><th>Learning Objectives</th><td>
                                          <ul>
                                            <li>Objective 1 for week ${week}</li>
                                            <li>Objective 2 for week ${week}</li>
                                          </ul>
                                        </td></tr>
                                        <tr><th>Resources</th><td>Textbooks, worksheets, teaching aids</td></tr>
                                        <tr><th>Activities</th><td>
                                          <p><strong>Day 1-2:</strong> Introduction and guided practice</p>
                                          <p><strong>Day 3-4:</strong> Independent practice and group work</p>
                                          <p><strong>Day 5:</strong> Review and assessment</p>
                                        </td></tr>
                                        <tr><th>Assessment</th><td>Observation, class work, homework</td></tr>
                                      </table>
                                    </div>
                                  `).join('')}
                                </div>
                              </div>
                              
                              <div class="footer">
                                <p>🌴 Penama Education Portal | Primary Curriculum Resources</p>
                                <p>Vanuatu Ministry of Education</p>
                              </div>
                            </body>
                            </html>
                          `;
                          
                          printWindow.document.write(html);
                          printWindow.document.close();
                          printWindow.onload = () => {
                            printWindow.print();
                            toast.success('Term pack ready to print!');
                          };
                        }}
                      >
                        <Printer className="w-4 h-4 mr-2" />
                        {isFrench ? "Imprimer le Pack Complet" : "Print Complete Pack"}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Individual Print Options */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="border border-blue-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3 mb-3">
                      <List className="w-8 h-8 text-blue-600" />
                      <div>
                        <h4 className="font-semibold">{isFrench ? "Programme de Travail" : "Scheme of Work"}</h4>
                        <p className="text-sm text-gray-500">{isFrench ? "Plan du trimestre" : "Term planning overview"}</p>
                      </div>
                    </div>
                    <Button variant="outline" className="w-full" onClick={printSchemeOfWork}>
                      <Printer className="w-4 h-4 mr-2" />
                      {isFrench ? "Imprimer" : "Print"}
                    </Button>
                  </CardContent>
                </Card>

                <Card className="border border-green-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3 mb-3">
                      <Calendar className="w-8 h-8 text-green-600" />
                      <div>
                        <h4 className="font-semibold">{isFrench ? "Plan Annuel" : "Yearly Planner"}</h4>
                        <p className="text-sm text-gray-500">{isFrench ? "Vue d'ensemble de l'année" : "Full year overview"}</p>
                      </div>
                    </div>
                    <Button variant="outline" className="w-full" onClick={printYearlyPlanner}>
                      <Printer className="w-4 h-4 mr-2" />
                      {isFrench ? "Imprimer" : "Print"}
                    </Button>
                  </CardContent>
                </Card>

                <Card className="border border-orange-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3 mb-3">
                      <Layers className="w-8 h-8 text-orange-600" />
                      <div>
                        <h4 className="font-semibold">{isFrench ? "Pack de Fiches" : "Worksheet Pack"}</h4>
                        <p className="text-sm text-gray-500">{isFrench ? "Fiches d'exercices" : "Practice worksheets"}</p>
                      </div>
                    </div>
                    <Button variant="outline" className="w-full" onClick={() => setActiveTab('worksheets')}>
                      <Layers className="w-4 h-4 mr-2" />
                      {isFrench ? "Voir les Fiches" : "View Worksheets"}
                    </Button>
                  </CardContent>
                </Card>

                <Card className="border border-red-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3 mb-3">
                      <FileQuestion className="w-8 h-8 text-red-600" />
                      <div>
                        <h4 className="font-semibold">{isFrench ? "Évaluations" : "Test Papers"}</h4>
                        <p className="text-sm text-gray-500">{isFrench ? "Évaluations du trimestre" : "Term assessments"}</p>
                      </div>
                    </div>
                    <Button variant="outline" className="w-full" onClick={() => setActiveTab('tests')}>
                      <FileQuestion className="w-4 h-4 mr-2" />
                      {isFrench ? "Voir les Tests" : "View Tests"}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
